import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ALPHA_AGENTS } from "@/data/agents";
import { COURSES } from "@/data/courses";
import { useQuieto } from "@/hooks/useQuieto";

/** LOS SEIS MENTORES, EN GRANDE Y EN MOVIMIENTO.
 *
 * DE DONDE VIENE. Hasta el 29 de octubre de 2026 eran seis circulos de 56 px en una rejilla.
 * Gabri: *«sirviendoles en tarjetas el triple de grandes que los circulos que hay ahora... o
 * modo carrusel automatico, que se desliza por cursor hacia la derecha o hacia la izquierda
 * dependiendo de si el cursor va hacia la mitad derecha o izquierda»*. Eligio el carrusel, y
 * por una razon de sitio: seis tarjetas de 168 px no caben en las siete columnas que ocupa
 * este bloque, y ensancharlo obligaria a bajar al profesor y «Para tu clase» a otra fila.
 *
 * COMO SE MUEVE. El raton no arrastra: **manda hacia donde mira**. Cuanto mas lejos del centro
 * esta el cursor, mas rapido corre la tira hacia ese lado; en el centro se para sola. Al sacar
 * el raton se detiene. No hay botones de flecha a proposito: con el dedo se arrastra, que es
 * lo que hace cualquiera con una tira de fotos en un telefono.
 *
 * Y DA LA VUELTA SIN COSTURA. La tira se pinta DOS VECES y el desplazamiento se normaliza a
 * la anchura de una tanda, asi que nunca se llega a un extremo y nunca hay un salto a la
 * vista. La segunda tanda es decorativa: `aria-hidden` y fuera del tabulador, porque si no un
 * lector de pantalla leeria seis mentores y luego otros seis iguales.
 *
 * LAS CARAS SON LAS DE TODA LA PLATAFORMA (`public/mentores/caras/`), las que eligio Gabri el
 * 29 de octubre.
 *
 * SOCRATES NO ES UN ENLACE, como no lo era antes: esta en todas las lecciones, asi que no hay
 * un sitio suyo al que llevar.
 */

const deLaCompetencia = (a) => COURSES.find((c) => c.slug === a.key) || {};

const LOS_SEIS = [
  { key: "socratico", mentor: "Sócrates", pie: "En toda lección", to: null },
  ...ALPHA_AGENTS.map((a) => ({
    key: a.key,
    mentor: a.mentor,
    pie: deLaCompetencia(a).nombreCompetencia,
    to: `/cursos/${a.key}`,
  })),
];

// Pixeles por fotograma en el borde del carril. Subio de 2.4 a 3.4 el 29 de octubre de 2026:
// *«haria que se mueva un pelin mas deprisa»*. El techo sigue siendo poder pulsar: por encima
// de 4 la cara que buscas se escapa antes de que llegue el dedo, y en el centro se para igual.
const TOPE = 3.4;

function Tarjeta({ m, decorativa }) {
  const cara = (
    <>
      <img
        src={`/mentores/caras/${m.key}.webp`}
        alt={decorativa ? "" : `${m.mentor}, mentor de ${m.pie}`}
        loading="lazy"
        decoding="async"
        width={360}
        height={360}
        className="rounded-caja w-full aspect-square object-cover bg-alpha-off"
      />
      <span className="block font-serif-display text-[17px] leading-tight mt-2.5">{m.mentor}</span>
      <span className="block font-mono-alpha text-[8px] uppercase tracking-[0.1em] text-alpha-black/40 mt-1">
        {m.pie}
      </span>
    </>
  );

  // EL ANCHO SALE DE UNA CUENTA Y NO DEL GUSTO, y conviene entenderla antes de tocarlo. La
  // tira se pinta DOS VECES para dar la vuelta sin costura, así que si una tanda cabe entera
  // en el carril se ven dos Sócrates a la vez. Pasó.
  //
  //   una tanda = 6 caras + 5 huecos de 12 = 6 × 128 + 60 = 828 px
  //   el carril, como mucho = 7 de las 12 columnas de 1320 = 758 px
  //
  // 828 contra 758: setenta píxeles de sobra. Bajar de 116 px, ensanchar la tarjeta a más de
  // siete columnas o estrechar los huecos devuelve el Sócrates duplicado.
  const comun = "w-[128px] shrink-0 text-center select-none";

  // TODOS SE LEVANTAN AL PASAR, SOCRATES TAMBIEN (29 oct 2026). Se quedaba quieto porque el
  // movimiento colgaba del `<Link>`, y el suyo no lo tiene: parecia apagado al lado de los
  // otros cinco. El movimiento es de la tarjeta y no del enlace, asi que vive aqui.
  const seLevanta = "block rounded-caja transition-transform hover:-translate-y-1";

  if (!m.to) {
    return (
      <li className={comun} aria-hidden={decorativa || undefined}>
        <span className={`group ${seLevanta}`}>{cara}</span>
      </li>
    );
  }
  return (
    <li className={comun} aria-hidden={decorativa || undefined}>
      <Link
        to={m.to}
        tabIndex={decorativa ? -1 : undefined}
        data-testid={decorativa ? undefined : `mentor-${m.key}`}
        aria-label={`Los cursos de ${m.pie}, con ${m.mentor}`}
        className={`group ${seLevanta}
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-alpha-black`}
      >
        {cara}
      </Link>
    </li>
  );
}

export default function CarruselDeMentores() {
  const carril = useRef(null);
  const velocidad = useRef(0);
  const animacion = useRef(0);

  // El ancho de UNA tanda, que es la mitad de todo lo que hay dentro.
  const anchoDeTanda = () => (carril.current ? carril.current.scrollWidth / 2 : 0);

  const normaliza = () => {
    const el = carril.current;
    if (!el) return;
    const tanda = anchoDeTanda();
    if (tanda <= 0) return;
    if (el.scrollLeft >= tanda) el.scrollLeft -= tanda;
    else if (el.scrollLeft < 0) el.scrollLeft += tanda;
  };

  const corre = () => {
    const el = carril.current;
    if (!el) return;
    if (velocidad.current !== 0) {
      el.scrollLeft += velocidad.current;
      normaliza();
    }
    animacion.current = requestAnimationFrame(corre);
  };

  const arranca = () => {
    if (!animacion.current) animacion.current = requestAnimationFrame(corre);
  };

  const para = () => {
    velocidad.current = 0;
    cancelAnimationFrame(animacion.current);
    animacion.current = 0;
  };

  useEffect(() => {
    // EMPIEZA EN LA SEGUNDA TANDA no: empieza en cero, pero con la tira ya desplazada medio
    // paso para que se vea que hay algo cortado a la derecha y se entienda que corre.
    const el = carril.current;
    if (el) el.scrollLeft = 0;
    return () => cancelAnimationFrame(animacion.current);
  }, []);

  // El ajuste del alumno manda sobre el del sistema: por eso no se pregunta aqui.
  const quieto = useQuieto();

  const mira = (e) => {
    if (quieto) return;
    const el = carril.current;
    if (!el) return;
    const caja = el.getBoundingClientRect();
    // -1 en el borde izquierdo, 0 en el centro, 1 en el derecho. La zona muerta del centro
    // evita que la tira tiemble cuando el raton pasa de largo por el medio.
    const d = ((e.clientX - caja.left) / caja.width) * 2 - 1;
    velocidad.current = Math.abs(d) < 0.15 ? 0 : Math.sign(d) * (Math.abs(d) ** 2) * TOPE;
    arranca();
  };

  return (
    <div
      className="relative mt-7 -mx-6 sm:-mx-8"
      onMouseMove={mira}
      onMouseLeave={para}
      data-testid="landing-carrusel-mentores"
    >
      <ul
        ref={carril}
        onScroll={normaliza}
        className="flex gap-3 overflow-x-auto px-6 sm:px-8 pb-1 [scrollbar-width:none]
                   [&::-webkit-scrollbar]:hidden"
      >
        {LOS_SEIS.map((m) => <Tarjeta key={m.key} m={m} />)}
        {LOS_SEIS.map((m) => <Tarjeta key={`eco-${m.key}`} m={m} decorativa />)}
      </ul>
    </div>
  );
}
